import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Tabs, Card, Badge, Row, Col, Spin, notification } from "antd"; // Added notification
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { fetchEscalationMatrix } from "../../Action/Actions";
import { getUserData } from "../login/LoginSlice";
import { selectFinancialYears, selectSelectedYearId } from "../../Redux/financialYearSlice";
import "./EscalationMatrix.css";
import {
  HomeOutlined,
  PhoneOutlined,
  MailOutlined,
  TeamOutlined,
  UserOutlined,
  FireOutlined,
  FileAddOutlined,
} from "@ant-design/icons";

const EscalationMatrix = () => {
  const dispatch = useDispatch();
  const user = useSelector(getUserData);
  const selectedYearId = useSelector(selectSelectedYearId);
  const yearList = useSelector(selectFinancialYears);

  const [activeTab, setActiveTab] = useState(null);
  const [selectedPolicyNumber, setSelectedPolicyNumber] = useState(null);
  const [escalationData, setEscalationData] = useState({});
  const [loading, setLoading] = useState(false);

  const selectedYear = useMemo(() => 
    yearList.find(year => year.id === selectedYearId)?.financial_year || "",
    [yearList, selectedYearId]
  );

  const policyTypes = useMemo(() => Object.keys(escalationData || {}), [escalationData]);

  useEffect(() => {
    if (user?.corporate_id && selectedYearId) {
      setLoading(true);
      dispatch(fetchEscalationMatrix(user.corporate_id))
        .then((data) => {
          // This logic is now safe because the thunk guarantees a valid object
          const { policy_escalation_matrix } = data;
          setEscalationData(policy_escalation_matrix || {});

          const firstPolicyType = Object.keys(policy_escalation_matrix || {})[0] || null;
          setActiveTab(firstPolicyType);
          
          const policiesForFirstType = policy_escalation_matrix?.[firstPolicyType] || {};
          const firstPolicyNumber = Object.keys(policiesForFirstType)[0] || null;
          
          setSelectedPolicyNumber(firstPolicyNumber);
        })
        .catch((err) => {
          // Clear data on error without showing error notification
          setEscalationData({}); // Clear data on error
          console.error("Error fetching escalation matrix:", err);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
        setEscalationData({});
        setActiveTab(null);
        setSelectedPolicyNumber(null);
    }
  }, [dispatch, user?.corporate_id, selectedYearId]);

  const handleTabChange = useCallback((key) => {
    setActiveTab(key);
    const policiesForNewTab = escalationData[key] || {};
    const firstPolicyNumberForNewTab = Object.keys(policiesForNewTab)[0] || null;
    setSelectedPolicyNumber(firstPolicyNumberForNewTab);
  }, [escalationData]);

  const handlePolicyNumberClick = useCallback((policyNumber) => {
    setSelectedPolicyNumber(policyNumber);
  }, []);

  const formatField = (fieldValue, fallback = "N/A") => fieldValue || fallback;

  const getBadgeColor = (level) => {
    switch (level) {
      case "Level 1": return "yellow";
      case "Level 2": return "#FFD700";
      case "Level 3": return "#FFCC00";
      case "Level 4": return "orange";
      case "Level 5": return "#FF7F00";
      case "Level 6": return "#FF4500";
      case "Level 7": return "#FF6347";
      case "Level 8": return "red";
      default: return "green";
    }
  };

  const getTabIcon = useCallback((type) => {
    switch (type) {
      case "GMC": return <TeamOutlined style={{ marginRight: 8 }} />;
      case "GTL": return <UserOutlined style={{ marginRight: 8 }} />;
      case "GPA": return <TeamOutlined style={{ marginRight: 8 }} />;
      case "Parent policy": return <TeamOutlined style={{ marginRight: 8 }} />;
      case "Topup": return <FileAddOutlined style={{ marginRight: 8 }} />;
      case "Fire": return <FireOutlined style={{ marginRight: 8 }} />;
      default: return null;
    }
  }, []);

  const EscalationLevels = ({ levels }) => (
    <Row gutter={[16, 16]} style={{ marginTop: "20px" }}>
      {levels?.map((level, index) => (
        <Col key={level.ref_escalation_matrices_level_id || index} xs={24} sm={12} md={8}>
          <Badge.Ribbon text={level.level} color={getBadgeColor(level.level)} placement="end">
            <Card className="escalation-card" title={level.ref_user_fullname} size="small">
              <div className="card-detail"><HomeOutlined style={{ marginRight: 8 }} />{formatField(level.Coporate_address)}</div>
              <div className="card-detail"><PhoneOutlined style={{ marginRight: 8 }} />{formatField(level.Phone_number)}</div>
              <div className="card-detail"><MailOutlined style={{ marginRight: 8 }} />{formatField(level.Coporate_email)}</div>
            </Card>
          </Badge.Ribbon>
        </Col>
      ))}
    </Row>
  );

  const tabItems = useMemo(() => policyTypes?.map((type) => {
    const policyNumbersForType = Object.keys(escalationData[type] || {});
    const levelsForSelectedPolicy = escalationData[type]?.[selectedPolicyNumber] || null;

    return {
      key: type,
      label: (<span style={{ display: "flex", alignItems: "center", gap: "5px" }}>{type}{getTabIcon(type)}</span>),
      children: (
        <>
          {policyNumbersForType.length > 0 ? (
            <>
              <Tabs
                className="policy-number-tabs"
                activeKey={selectedPolicyNumber}
                onChange={handlePolicyNumberClick}
                items={policyNumbersForType.map((pNumber) => ({ 
                  key: pNumber, 
                  label: pNumber === 'N/A' ? (
                    <span
                      style={{
                        backgroundColor: "#FFFF00",
                        color: "#000000E0",
                        padding: "2px 8px",
                        fontSize: "12px",
                        fontFamily: '"Plus Jakarta Sans", sans-serif',
                        borderRadius: "4px",
                        display: "inline-block",
                      }}
                    >
                      Policy Number not issued
                    </span>
                  ) : pNumber
                }))}
              />
              {levelsForSelectedPolicy ? (
                <EscalationLevels levels={levelsForSelectedPolicy} />
              ) : (
                <div className="escalation-message">Please select a policy number to view its escalation matrix.</div>
              )}
            </>
          ) : (
            <div className="escalation-message">No escalation matrix found for this category.</div>
          )}
        </>
      ),
    };
  }), [policyTypes, escalationData, selectedPolicyNumber, handlePolicyNumberClick, getTabIcon]);

  return (
    <Spin spinning={loading}>
      <div className="escalation-matrix-container">
        <div className="escalation-heading">Escalation Matrix</div>
        <div className="escalation-content">
          <Row className="escalation-row-header">
            <span style={{ fontWeight: "700", fontSize: "20px" }}>
              {user?.corporate_name || "Guest"}
            </span>
            <span style={{ marginLeft: "8px" }}>
              (Financial Year: {selectedYear})
            </span>
          </Row>
        </div>
        {policyTypes.length > 0 && activeTab ? (
          <Tabs
            activeKey={activeTab}
            onChange={handleTabChange}
            className="esc-tab"
            items={tabItems}
          />
        ) : (
          !loading && (
            <Card style={{ marginTop: "20px" }}>
              <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
                <p style={{ fontSize: "16px", fontWeight: "500" }}>No escalation matrix data available for {selectedYear}</p>
              </div>
            </Card>
          )
        )}
      </div>
    </Spin>
  );
};

export default EscalationMatrix;